import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, Dialog, DialogContent,
  CircularProgress, Tooltip,
} from '@mui/material';
import {
  PhotoLibrary as PhotoLibraryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from '../../api/axios';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const diff = (today - d) / (1000 * 60 * 60 * 24);
  if (diff < 1) return 'Today';
  if (diff < 2) return 'Yesterday';
  if (diff < 7) return `${Math.floor(diff)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isImage = (entry) => {
  const t = (entry.file_type || '').toLowerCase();
  return t.startsWith('image/') || /\.(png|jpe?g|gif|webp|heic)$/i.test(entry.original_filename || '');
};

const ProjectTimeline = ({ projectId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEntry, setOpenEntry] = useState(null);

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/projects/${projectId}/timeline`);
      setEntries(response.data.entries || []);
    } catch (err) {
      // Silent — timeline is supplementary, not blocking.
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const photos = entries.filter(isImage);
  if (photos.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <PhotoLibraryIcon color="primary" />
        <Typography variant="h6">Project Timeline</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {photos.length} photo{photos.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          overflowX: 'auto',
          pb: 1.5,
          scrollSnapType: 'x mandatory',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 3 },
        }}
      >
        {photos.map((entry) => (
          <Tooltip
            key={entry.id}
            title={`${entry.item_title} — ${entry.room_name}`}
            arrow
          >
            <Box
              onClick={() => setOpenEntry(entry)}
              sx={{
                position: 'relative',
                flex: '0 0 auto',
                width: 140,
                height: 140,
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                scrollSnapAlign: 'start',
                boxShadow: 1,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              }}
            >
              <Box
                component="img"
                src={entry.file_path}
                alt={entry.original_filename}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  color: 'white',
                  px: 1, py: 0.5,
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>{fmtDate(entry.created_at)}</span>
                <span style={{ opacity: 0.85 }}>{entry.room_name}</span>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>

      <Dialog
        open={!!openEntry}
        onClose={() => setOpenEntry(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative', backgroundColor: '#000' }}>
          <IconButton
            onClick={() => setOpenEntry(null)}
            sx={{
              position: 'absolute', top: 8, right: 8,
              backgroundColor: 'rgba(0,0,0,0.55)', color: 'white',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
          {openEntry && (
            <>
              <Box
                component="img"
                src={openEntry.file_path}
                alt={openEntry.original_filename}
                sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block' }}
              />
              <Box sx={{ p: 2, color: 'white', backgroundColor: 'rgba(0,0,0,0.85)' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {openEntry.item_title}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {openEntry.room_name} · uploaded by {openEntry.user_first_name} {openEntry.user_last_name} · {new Date(openEntry.created_at).toLocaleString()}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProjectTimeline;
